"use client";

import type { StackOverflowUserSchema } from "@/lib/api/model";

import { formatLongDateFromUnix } from "@/lib/date/format-long-date";

import { AccountHeader } from "./account-header";

export interface StackOverflowBlockProps {
  profile: StackOverflowUserSchema;
}

const BADGE_TIERS: Array<{
  key: "gold" | "silver" | "bronze";
  label: string;
  dotClass: string;
}> = [
  { key: "gold", label: "Gold", dotClass: "bg-[#F1B400]" },
  { key: "silver", label: "Silver", dotClass: "bg-[#B4B8BC]" },
  { key: "bronze", label: "Bronze", dotClass: "bg-[#D1884F]" },
];

export function StackOverflowBlock({ profile }: StackOverflowBlockProps) {
  const memberSince = formatLongDateFromUnix(profile.creation_date);
  const reputation = profile.reputation ?? 0;

  const badges = BADGE_TIERS.filter((tier) => {
    const count = profile.badge_counts?.[tier.key] ?? 0;
    return count > 0;
  });

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <AccountHeader
        network="stackoverflow.com"
        username={profile.display_name}
        externalUrl={profile.link}
        tooltip="Public profile data fetched live from Stack Overflow."
        meta={
          <>
            {memberSince && <span>Member since {memberSince}</span>}
            <span>
              Reputation Points
              <span className="px-1 text-foreground/30">•</span>
              <span className="font-medium text-foreground">
                {reputation.toLocaleString()}
              </span>
            </span>
          </>
        }
      />

      {badges.length > 0 && (
        <div className="flex w-full min-w-0 flex-col gap-2">
          <p className="text-sm font-medium leading-5 text-foreground">
            Earned Badges
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] leading-[18px] tracking-[-0.13px] text-foreground/70">
            {badges.map((tier) => {
              const count = profile.badge_counts?.[tier.key] ?? 0;
              return (
                <span
                  key={tier.key}
                  className="inline-flex items-center gap-1.5"
                >
                  <span className="text-foreground">{tier.label}</span>
                  <span
                    aria-hidden
                    className={`size-2 shrink-0 rounded-full ${tier.dotClass}`}
                  />
                  <span className="text-foreground/55">
                    ({count.toLocaleString()})
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
